<template>
  <wui-btn
    data-test="chat-forward-message-button"
    class="w-full"
    variant="brand"
    state="filled"
    :title="$t('chat.sendForward')"
    :disabled="(selectedContacts.length === 0 || sendDisabled)!"
    @click="send()"
  >
    {{ $t('chat.sendForward') }}
  </wui-btn>
</template>

<script setup lang="ts">
import { WuiBtn, closeDialogKey, safeInject, useRenderNotifier } from '@wui/common-library'
import { HistoryMessageItem, MessageIncomingEvent, MessageOutgoingEvent, PServed } from '@wui/im'
import { nextTick } from 'vue'

import { useStoreRooms } from '@/entities/room'
import { useRoom } from '@/entities/use-room'

import { ChatFile } from '@/shared/types/files'
import { CHAT_QUERY_SCROLL_TO, CHAT_QUERY_SCROLL_TO_BOTTOM } from '@/shared/utils/constants'
import { createLinksFromText } from '@/shared/utils/create-links-from-text'
import { optionalPush } from '@/shared/utils/router'
import { fromPServedUserIfRoom } from '@/shared/utils/uri'
import { getChatRoute } from '@/shared/utils/url'

const props = withDefaults(
  defineProps<{
    selectedContacts: PServed[]
    chatEvents: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>[]
    messageText: string
    chatFiles?: ChatFile[]
    sendDisabled?: boolean
  }>(),
  { chatFiles: () => [], sendDisabled: false },
)

const emit = defineEmits<{
  (event: 'sent'): void
}>()

const roomApi = useRoom()
const storeRooms = useStoreRooms()
const { notifyError } = useRenderNotifier()
const closeModal = safeInject(closeDialogKey)

const openForwardTargetChat = (pServed: PServed) => {
  const room = storeRooms.findByPServed(pServed)
  optionalPush(getChatRoute(room ?? fromPServedUserIfRoom(pServed), {
    query: { [CHAT_QUERY_SCROLL_TO]: CHAT_QUERY_SCROLL_TO_BOTTOM },
  }))
}

const send = () => {
  const chatFiles = props.chatFiles ?? []
  props.selectedContacts.forEach((pServed) => {
    if (props.chatEvents.length > 1) {
      props.chatEvents.forEach((event, index) => {
        const text = index === 0 ? createLinksFromText(props.messageText) : ''
        const filesToSend = index === 0 ? chatFiles : undefined
        roomApi.sendForwardMessage(event, pServed, text, filesToSend)
          .catch((e) => {
            notifyError(e.message)
          })
      })
    } else if (props.chatEvents[0]) {
      roomApi.sendForwardMessage(props.chatEvents[0], pServed, createLinksFromText(props.messageText) ?? '', chatFiles)
        .catch((e) => {
          notifyError(e.message)
        })
    }
  })

  const [targetPServed] = props.selectedContacts
  emit('sent')
  closeModal(true)

  if (props.selectedContacts.length === 1 && targetPServed) {
    void nextTick(() => {
      openForwardTargetChat(targetPServed)
    })
  }
}
</script>
